import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { backend } from '../data';
import type { BhStore } from '@bhorosha/data';
import { PEOPLE_KEY, displayLogin, isUsernameLogin } from '@bhorosha/data';
import { AppLoginsTab } from './AppLogins';
import { ReminderTimes } from './ReminderTimes';
import type { Invite, Member, Membership, Role, Session } from '@bhorosha/data';
import { t, tErr } from '@bhorosha/i18n';
import { ErrorText, Field, Modal, Notice, Spinner, initials } from '../ui/components';
import { fmtDMY } from '../ui/dates';

type Tab = 'members' | 'invite' | 'apps' | 'settings';
type Person = { id: string; name: string; age?: string; gender?: string; relation?: string; notify_user_ids?: string[] };

/** Relation of each tracked person to the family owner. Stored on the person as `relation`. */
export const RELATIONS = ['self', 'spouse', 'father', 'mother', 'son', 'daughter', 'brother', 'sister',
  'grandfather', 'grandmother', 'grandson', 'granddaughter', 'fatherInLaw', 'motherInLaw',
  'sonInLaw', 'daughterInLaw', 'uncle', 'aunt', 'nephew', 'niece', 'cousin', 'other'] as const;

function inviteUrl(token: string) { return `${location.origin}/invite/${token}`; }

function PatientPicker({ people, all, ids, onChange }: { people: Person[]; all: boolean; ids: string[]; onChange: (all: boolean, ids: string[]) => void }) {
  return (
    <div className="bh-access">
      <label className="bh-radio"><input type="radio" checked={all} onChange={() => onChange(true, ids)} /> {t('fam.access.all')}</label>
      <label className="bh-radio"><input type="radio" checked={!all} onChange={() => onChange(false, ids)} /> {t('fam.access.some')}</label>
      {!all && (
        <div className="bh-checks">
          {people.map(p => (
            <label key={p.id} className="bh-check">
              <input type="checkbox" checked={ids.includes(p.id)}
                onChange={e => onChange(false, e.target.checked ? [...ids, p.id] : ids.filter(x => x !== p.id))} />
              {p.name || 'Unnamed'}
            </label>
          ))}
          {!ids.length && <span className="bh-small bh-warn">{t('fam.access.none')}</span>}
        </div>
      )}
    </div>
  );
}

function RoleSelect({ value, onChange }: { value: Exclude<Role, 'owner'>; onChange: (r: Exclude<Role, 'owner'>) => void }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value as Exclude<Role, 'owner'>)}>
      <option value="editor">{t('role.editor')}</option>
      <option value="viewer">{t('role.viewer')}</option>
    </select>
  );
}

export function FamilyPanel({ session, membership, store, onClose, onChanged }: {
  session: Session; membership: Membership; store: BhStore; onClose: () => void; onChanged: () => Promise<Membership[]>;
}) {
  const isOwner = membership.role === 'owner';
  const [tab, setTab] = useState<Tab>('members');
  const [members, setMembers] = useState<Member[] | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const fid = membership.familyId;

  const readPeople = useCallback((): Person[] => {
    try { return (JSON.parse(store.getItem(PEOPLE_KEY) || '[]') as Person[]).map(p => ({ id: p.id, name: p.name, age: p.age, gender: p.gender, relation: p.relation, notify_user_ids: p.notify_user_ids })); }
    catch { return []; }
  }, [store]);
  const [people, setPeople] = useState<Person[]>(readPeople);
  const ownerName = (members || []).find(m => m.role === 'owner')?.displayName || t('role.owner');

  /** Save a person's relation to the owner, then let the app screens re-read the people list. */
  function setRelation(id: string, relation: string) {
    try {
      const all = JSON.parse(store.getItem(PEOPLE_KEY) || '[]') as Record<string, unknown>[];
      const p = all.find(x => x.id === id);
      if (!p) return;
      if (relation) p.relation = relation; else delete p.relation;
      store.setItem(PEOPLE_KEY, JSON.stringify(all));
      window.__bhReload?.();
      setPeople(readPeople());
      setMsg(t('fam.saved'));
    } catch (e) { setErr(tErr(e)); }
  }
  const nameOf = (id: string) => people.find(p => p.id === id)?.name || id;

  const reload = useCallback(async () => {
    try {
      setMembers(await backend.listMembers(fid));
      if (isOwner) setInvites(await backend.listInvites(fid));
    } catch (e) { setErr(tErr(e)); }
  }, [fid, isOwner]);
  useEffect(() => { void reload(); }, [reload]);

  async function run(fn: () => Promise<unknown>, ok = t('fam.saved')) {
    setErr(''); setMsg('');
    try { await fn(); await reload(); await onChanged(); setMsg(ok); } catch (e) { setErr(tErr(e)); }
  }

  return (
    <Modal title={`${t('fam.title')} — ${membership.familyName}`} onClose={onClose} wide>
      <div className="bh-tabs" role="tablist">
        <button className={tab === 'members' ? 'on' : ''} onClick={() => setTab('members')}>{t('fam.tab.members')}</button>
        {isOwner && <button className={tab === 'invite' ? 'on' : ''} onClick={() => setTab('invite')}>{t('fam.tab.invite')}{invites.length ? ` (${invites.length})` : ''}</button>}
        {isOwner && <button className={tab === 'apps' ? 'on' : ''} onClick={() => setTab('apps')}>{t('fam.tab.apps')}</button>}
        <button className={tab === 'settings' ? 'on' : ''} onClick={() => setTab('settings')}>{t('fam.tab.settings')}</button>
      </div>
      {msg && <Notice tone="ok">{msg}</Notice>}
      <ErrorText>{err}</ErrorText>

      {tab === 'members' && (members === null ? <Spinner /> : (
        <div className="bh-list">
          {!isOwner && <Notice>{t('fam.ownerOnly')}</Notice>}
          {members.map(m => (
            <MemberRow key={m.userId} m={m} me={m.userId === session.userId} isOwner={isOwner} people={people} nameOf={nameOf}
              onUpdate={patch => run(() => backend.updateMember(fid, m.userId, patch))}
              onRemove={() => { if (confirm(t('fam.removeConfirm', { name: m.displayName || m.email }))) void run(() => backend.removeMember(fid, m.userId)); }}
              onMakeOwner={() => { if (confirm(t('fam.makeOwnerConfirm', { name: m.displayName || m.email }))) void run(async () => { await backend.transferOwnership(fid, m.userId); location.reload(); }); }}
            />
          ))}
          {!isOwner && (
            <button className="bh-btn bh-btn-danger" onClick={async () => {
              if (!confirm(t('fam.leaveConfirm', { family: membership.familyName }))) return;
              try { await backend.removeMember(fid, session.userId); localStorage.removeItem(`bh.activeFamily.${session.userId}`); location.href = '/'; }
              catch (e) { setErr(tErr(e)); }
            }}>{t('fam.leave')}</button>
          )}
        </div>
      ))}

      {tab === 'invite' && isOwner && (
        <InviteTab fid={fid} people={people} invites={invites} nameOf={nameOf} onCreated={reload}
          onRevoke={id => run(() => backend.revokeInvite(id))} setErr={setErr} />
      )}

      {tab === 'apps' && isOwner && <AppLoginsTab fid={fid} store={store} people={people} onChanged={async () => { await reload(); await onChanged(); }} />}

      {tab === 'settings' && <ReminderTimes store={store} isOwner={isOwner} />}
      {tab === 'settings' && <SettingsTab membership={membership} people={people} isOwner={isOwner} run={run}
        canEdit={membership.role !== 'viewer'} ownerName={ownerName} onRelation={setRelation} />}
    </Modal>
  );
}

function MemberRow({ m, me, isOwner, people, nameOf, onUpdate, onRemove, onMakeOwner }: {
  m: Member; me: boolean; isOwner: boolean; people: Person[]; nameOf: (id: string) => string;
  onUpdate: (p: { role?: Exclude<Role, 'owner'>; allPatients?: boolean; patientIds?: string[] }) => void;
  onRemove: () => void; onMakeOwner: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [all, setAll] = useState(m.allPatients);
  const [ids, setIds] = useState(m.patientIds);
  const canEdit = isOwner && m.role !== 'owner';
  const accessText = m.role === 'owner' || m.allPatients ? t('fam.access.all') : (m.patientIds.map(nameOf).join(', ') || t('fam.access.none'));
  return (
    <div className="bh-member">
      <span className="bh-acct-avatar">{initials(m.displayName || m.email)}</span>
      <div className="bh-member-main">
        <div className="bh-strong">{m.displayName || m.email} {me && <span className="bh-chip">{t('fam.you')}</span>}</div>
        <div className="bh-small bh-muted">{isUsernameLogin(m.email) ? `📱 ${t('app.username')}: ${displayLogin(m.email)}` : m.email}</div>
        <div className="bh-small">{t('fam.access')}: {accessText}</div>
        {editing && (
          <div className="bh-member-edit">
            <PatientPicker people={people} all={all} ids={ids} onChange={(a, i) => { setAll(a); setIds(i); }} />
            <div className="bh-row">
              <button className="bh-btn bh-btn-primary" disabled={!all && !ids.length}
                onClick={() => { onUpdate({ allPatients: all, patientIds: all ? [] : ids }); setEditing(false); }}>{t('common.save')}</button>
              <button className="bh-btn bh-btn-ghost" onClick={() => setEditing(false)}>{t('common.cancel')}</button>
            </div>
          </div>
        )}
      </div>
      <div className="bh-member-side">
        {canEdit
          ? <RoleSelect value={m.role as Exclude<Role, 'owner'>} onChange={r => onUpdate({ role: r })} />
          : <span className="bh-chip bh-chip-strong">{t(`role.${m.role}`)}</span>}
        {canEdit && !editing && <button className="bh-link" onClick={() => setEditing(true)}>{t('fam.access')}…</button>}
        {canEdit && <button className="bh-link" onClick={onMakeOwner}>{t('fam.makeOwner')}</button>}
        {canEdit && <button className="bh-link bh-warn" onClick={onRemove}>{t('fam.remove')}</button>}
      </div>
    </div>
  );
}

function InviteTab({ fid, people, invites, nameOf, onCreated, onRevoke, setErr }: {
  fid: string; people: Person[]; invites: Invite[]; nameOf: (id: string) => string;
  onCreated: () => Promise<void>; onRevoke: (id: string) => void; setErr: (s: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Exclude<Role, 'owner'>>('editor');
  const [all, setAll] = useState(true);
  const [ids, setIds] = useState<string[]>([]);
  const [created, setCreated] = useState<Invite | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr('');
    if (!all && !ids.length) { setErr(t('fam.invite.pickPatient')); return; }
    setBusy(true);
    try {
      const inv = await backend.createInvite(fid, { email, role, allPatients: all, patientIds: all ? [] : ids });
      setCreated(inv); setEmail(''); await onCreated();
    } catch (ex) { setErr(tErr(ex)); } finally { setBusy(false); }
  }

  return (
    <div>
      <h3 className="bh-h3">{t('fam.invite.title')}</h3>
      <p className="bh-small bh-muted">{t('fam.invite.help')}</p>
      <form className="bh-form" onSubmit={submit}>
        <Field label={t('fam.invite.email')}><input type="email" value={email} onChange={e => setEmail(e.target.value)} required /></Field>
        <Field label={t('fam.invite.role')}>
          <RoleSelect value={role} onChange={setRole} />
          <span className="bh-field-hint">{t(`role.${role}.desc`)}</span>
        </Field>
        <Field label={t('fam.access')}><PatientPicker people={people} all={all} ids={ids} onChange={(a, i) => { setAll(a); setIds(i); }} /></Field>
        <button className="bh-btn bh-btn-primary" disabled={busy}>{t('fam.invite.create')}</button>
      </form>
      {created && <InviteLink inv={created} />}

      <h3 className="bh-h3">{t('fam.invite.pending')}</h3>
      {!invites.length && <p className="bh-small bh-muted">{t('fam.invite.none')}</p>}
      {invites.map(inv => (
        <div className="bh-member" key={inv.id}>
          <div className="bh-member-main">
            <div className="bh-strong">{inv.email}</div>
            <div className="bh-small bh-muted">{t(`role.${inv.role}`)} · {inv.allPatients ? t('fam.access.all') : inv.patientIds.map(nameOf).join(', ')} · {t('fam.invite.expires', { date: fmtDMY(inv.expiresAt) })}</div>
            <InviteLink inv={inv} compact />
          </div>
          <div className="bh-member-side"><button className="bh-link bh-warn" onClick={() => onRevoke(inv.id)}>{t('fam.invite.revoke')}</button></div>
        </div>
      ))}
    </div>
  );
}

function InviteLink({ inv, compact }: { inv: Invite; compact?: boolean }) {
  const url = inviteUrl(inv.token);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { prompt('Copy this link', url); }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const share = async () => {
    try { await navigator.share({ title: 'Bhorosha', text: t('fam.invite.shareText', { url }), url }); } catch { /* cancelled */ }
  };
  return (
    <div className={compact ? 'bh-invite-link compact' : 'bh-invite-link'}>
      {!compact && <div className="bh-small">{t('fam.invite.ready', { email: inv.email })}</div>}
      <div className="bh-row">
        <input readOnly value={url} onFocus={e => e.currentTarget.select()} />
        <button type="button" className="bh-btn bh-btn-ghost" onClick={copy}>{copied ? t('common.copied') : t('common.copy')}</button>
        {typeof navigator.share === 'function' && <button type="button" className="bh-btn bh-btn-ghost" onClick={share}>{t('common.share')}</button>}
      </div>
    </div>
  );
}

function SettingsTab({ membership, people, isOwner, run, canEdit, ownerName, onRelation }: {
  membership: Membership; people: Person[]; isOwner: boolean; run: (fn: () => Promise<unknown>) => Promise<void>;
  canEdit: boolean; ownerName: string; onRelation: (id: string, relation: string) => void;
}) {
  const [name, setName] = useState(membership.familyName);
  return (
    <div>
      {isOwner && (
        <form className="bh-form" onSubmit={e => { e.preventDefault(); void run(() => backend.renameFamily(membership.familyId, name.trim())); }}>
          <Field label={t('fam.settings.name')}>
            <div className="bh-row"><input value={name} onChange={e => setName(e.target.value)} required /><button className="bh-btn bh-btn-ghost">{t('fam.settings.rename')}</button></div>
          </Field>
        </form>
      )}
      <h3 className="bh-h3">{t('fam.settings.people')}</h3>
      <div className="bh-people-table" role="table" aria-label={t('fam.settings.people')}>
        <div className="bh-pt-row bh-pt-head" role="row">
          <span role="columnheader">{t('fam.person.name')}</span>
          <span role="columnheader">{t('fam.person.age')}</span>
          <span role="columnheader">{t('fam.person.gender')}</span>
          <span role="columnheader">{t('fam.person.relation', { owner: ownerName })}</span>
        </div>
        {people.map(p => (
          <div className="bh-pt-row" role="row" key={p.id}>
            <span role="cell" className="bh-pt-name"><span className="bh-acct-avatar">{initials(p.name)}</span> {p.name || 'Unnamed'}</span>
            <span role="cell" data-label={t('fam.person.age')}>{p.age || '—'}</span>
            <span role="cell" data-label={t('fam.person.gender')}>{!p.gender ? '—' : ['male', 'female', 'other'].includes(p.gender.toLowerCase()) ? t(`onb.${p.gender.toLowerCase()}`) : p.gender}</span>
            <span role="cell" data-label={t('fam.person.relation', { owner: ownerName })}>
              {canEdit ? (
                <select value={p.relation || ''} onChange={e => onRelation(p.id, e.target.value)} aria-label={t('fam.person.relation', { owner: ownerName })}>
                  <option value="">{t('fam.person.choose')}</option>
                  {RELATIONS.map(r => <option key={r} value={r}>{t(`rel.${r}`)}</option>)}
                </select>
              ) : (p.relation ? t(`rel.${p.relation}`) : '—')}
            </span>
          </div>
        ))}
      </div>
      <p className="bh-small bh-muted">{t('fam.settings.peopleHint')}</p>
      {isOwner && (
        <button className="bh-btn bh-btn-danger" onClick={() => {
          const typed = prompt(t('fam.settings.deleteConfirm', { family: membership.familyName }));
          if (typed !== null && typed.trim() === membership.familyName.trim()) {
            void run(async () => { await backend.deleteFamily(membership.familyId); location.href = '/'; });
          }
        }}>{t('fam.settings.delete')}</button>
      )}
    </div>
  );
}
